/**
 * svgInsolationColor — applies the "insolation" color scheme to compartment fills.
 *
 * Each `path.compartment` gets `style.fill` based on the sum of the compartment's
 * `sun_light_ranges` (rounded down, clamped 0..10) → CSS var bucket.
 *
 * Colors are read from CSS variables injected by `useThemeVars`:
 *   --bim-color-insolation-{0..10}  for hour buckets 0–10
 *   --bim-color-insolation-null      for window_area === 0 (no window)
 * These vars are derived from `defaultStyle.insolation_colors` and can be
 * overridden per-project via panel-styles.json.
 *
 * Mirrors {@link applyColorSchemeMutations} — uses inline `style.fill`, which
 * wins over the .bim-plan CSS-var cascade without specificity fights.
 */

import { sumSunLightRanges } from '../../../shared/utils/sunLightUtils.js'

// CSS variable helpers — injected by useThemeVars from defaultStyle.insolation_colors
const _cssVarBucket      = (key) => `var(--bim-color-insolation-${key})`
const _cssVarBucketMuted = (key) => `var(--bim-color-insolation-${key}-muted)`
const _CSS_VAR_NULL       = 'var(--bim-color-insolation-null)'
const _CSS_VAR_NULL_MUTED = 'var(--bim-color-insolation-null-muted)'

/**
 * Returns the insolation bucket for a compartment.
 * Returns `null` when the room has no windows (window_area ≤ 0).
 * Returns 0–10 otherwise.
 *
 * @param {object|undefined} comp
 * @returns {number|null}
 */
export function getBucket(comp) {
  const windowArea = Number(comp?.window_area) || 0
  if (windowArea <= 0) return null
  const sumHours = Math.floor(sumSunLightRanges(comp.sun_light_ranges))
  return Math.min(10, Math.max(0, sumHours))
}

const _activeFill = (bucket) => bucket === null ? _CSS_VAR_NULL       : _cssVarBucket(bucket)
const _mutedFill  = (bucket) => bucket === null ? _CSS_VAR_NULL_MUTED : _cssVarBucketMuted(bucket)

/**
 * @param {SVGElement} svgElement
 * @param {Array} productCompartments — product.compartments (number, sun_light_ranges, window_area)
 */
export function applyInsolationColorMutations(svgElement, productCompartments = []) {
  const paths = svgElement.querySelectorAll('path.compartment')

  // Build lookup: compartment number → product compartment data
  const byNumber = {}
  for (const c of productCompartments) {
    byNumber[Number(c?.number)] = c
  }

  for (const path of paths) {
    const number = Number(path.getAttribute('number'))
    const comp = byNumber[number]

    if (!comp) {
      path.style.fill = ''
      continue
    }

    path.style.fill = _activeFill(getBucket(comp))
  }
}

/**
 * Clear inline fills set by {@link applyInsolationColorMutations}.
 */
export function clearInsolationColorMutations(svgElement) {
  const paths = svgElement.querySelectorAll('path.compartment')
  for (const path of paths) {
    path.style.fill = ''
  }
}

/**
 * Apply or clear the hover-highlight effect on compartment fills.
 *
 * When `hoveredHour` is a number: paths whose hour bucket matches get their
 * active fill restored; all others receive the `-muted` fill variant (solid,
 * lighter — no opacity change, safe in PlanView).
 *
 * When `hoveredHour` is null: all paths are restored to their active fill.
 *
 * @param {SVGElement}  svgElement
 * @param {Array}       productCompartments
 * @param {number|null} hoveredHour  — 0–10 or null
 */
export function applyInsolationHoverHighlight(svgElement, productCompartments, hoveredHour) {
  const paths = svgElement.querySelectorAll('path.compartment')

  const byNumber = {}
  for (const c of productCompartments) {
    byNumber[Number(c?.number)] = c
  }

  for (const path of paths) {
    const number = Number(path.getAttribute('number'))
    const comp   = byNumber[number]
    const bucket = getBucket(comp)

    if (hoveredHour === null || hoveredHour === undefined) {
      // Restore all fills
      path.style.fill = comp ? _activeFill(bucket) : ''
    } else {
      // Active bucket → base fill; everything else (incl. no-window, no-data) → muted fill
      const isMatch = comp && bucket === hoveredHour
      path.style.fill = isMatch ? _activeFill(bucket) : _mutedFill(bucket)
    }
  }
}
